/**
 * Republish records a device already published, at the versions it published them.
 *
 * WHY THIS EXISTS
 *
 * A cloud change log is the only copy of business state (`sync_changes` is the
 * event log; `sync_fetch_snapshot` is derived from it). A log that loses entries
 * loses records — permanently, because a device that restored an earlier
 * snapshot is pinned at the head cursor and `sync_pull_changes` never asks it for
 * a snapshot again, while `applyChanges` never REMOVES a row on behalf of a
 * snapshot. The device is then current, and wrong, and the only place the lost
 * rows still exist is another device's IndexedDB.
 *
 * This module turns that device into a source again WITHOUT inventing a version:
 * every change it queues carries the version already recorded for the record —
 * the version its own accepted outbox entry states, or failing that the version
 * stored on the row. `nextVersion` is deliberately never called here. A version
 * bump would claim a new edit happened, which would then collide with the real
 * history of every other device; a republish at the SAME version is, by
 * construction, "the change that was already accepted, sent again".
 *
 * WHAT IT DOES NOT DO
 *
 *  - It does not push. It queues outbox entries and stops. Uploading is the
 *    sync engine's job, on the operator's explicit action, so a recovery cannot
 *    become a network side effect of pressing a button labelled "preview".
 *  - It does not touch cursors, conflicts, or any row's `syncVersion`.
 *  - It does not guess. Anything it cannot establish from the database is a
 *    blocker, and one blocker refuses the whole run.
 *
 * WHY THE OUTBOX, NOT THE ROW, IS THE VERSION SOURCE
 *
 * `recordBusinessMutations` stamps `syncChangeId` on the row but never writes
 * `syncVersion`, and `issueDocument` bulk-adds document lines with no
 * `syncVersion` at all — so a line this device published at version 1 can still
 * read `syncVersion: 0` locally. Trusting the row first would republish such a
 * line at 0, and the server rejects the ENTIRE batch on `v_version < 1`.
 */

import type { DNRDatabase } from '@data/db/database';
import type { Setting, SyncOutboxEntry } from '@data/db/types';
import type { SyncEntityType } from '@domain/sync/changeModel';
import { documentLineSchema, documentSchema } from '@domain/schemas/primitives';
import { DEVICE_ID_SETTING_ID } from './deviceIdentity';
import { tableNameFor } from './applyChanges';
import { recordBusinessMutations, type RecordBusinessMutation } from './recordBusinessMutation';

/** One record to put back into the log. */
export interface RepublishTarget {
  readonly entityType: SyncEntityType;
  readonly entityId: string;
}

export interface RepublishRequest {
  readonly businessId: string;
  /** The device doing the republish. Recorded on every entry it queues. */
  readonly deviceId: string;
  readonly targets: readonly RepublishTarget[];
  /** Stamped on the durable marker only. Never used as a change's `changedAt`. */
  readonly now: string;
}

export type RepublishPreviewRequest = Omit<RepublishRequest, 'deviceId'>;

export type RepublishOperation = 'UPSERT' | 'DELETE';

/**
 * Where the version came from, in the order it is trusted.
 *
 * `documentLine-upsert-invariant` is the last resort and only for a line's
 * UPSERT: `documentService.issueDocument` publishes every line UPSERT at
 * `version: 1` (see documentService.ts, the line change beside `bulkAdd`), and a
 * line is never edited in place — an edit deletes the line and creates a new one
 * with a new id. So a line with no recorded version was published at 1.
 */
export type RepublishVersionSource = 'outbox' | 'row' | 'documentLine-upsert-invariant';

export type RepublishBlockerCode =
  | 'NOTHING_TO_REPUBLISH'
  | 'NO_TABLE'
  | 'FOREIGN_ROW'
  | 'MISSING_RECORD'
  | 'VERSION_UNRESOLVED'
  | 'DELETE_WITHOUT_TIMESTAMP'
  | 'CHANGED_AT_UNRESOLVED'
  | 'ALREADY_PENDING'
  | 'ALREADY_EXECUTED'
  | 'DEVICE_ID_UNAVAILABLE'
  | 'UNAUTHORIZED_DEVICE'
  | 'PLAN_DEVICE_MISMATCH'
  | 'FINGERPRINT_CHANGED'
  | 'INVALID_SCOPE'
  | 'INVALID_PAYLOAD'
  | 'INVALID_RELATIONSHIP'
  | 'DELETE_PROVENANCE_INVALID';

export interface RepublishBlocker {
  readonly code: RepublishBlockerCode;
  readonly detail: string;
  readonly entityType?: SyncEntityType;
  readonly entityId?: string;
}

export interface RepublishItem {
  readonly entityType: SyncEntityType;
  readonly entityId: string;
  readonly operation: RepublishOperation;
  /** The version ALREADY recorded for this record. Never a new one. */
  readonly version: number;
  readonly versionSource: RepublishVersionSource;
  readonly changedAt: string;
  /** The stored row, verbatim, for an UPSERT. Absent for a DELETE. */
  readonly payload?: Record<string, unknown>;
  /** The original deletion timestamp, for a DELETE. Absent for an UPSERT. */
  readonly deletedAt?: string;
  /** The outbox entry whose version was republished, when one existed. */
  readonly fromChangeId: string | null;
}

export interface RepublishPlan {
  readonly businessId: string;
  readonly deviceId: string;
  /** Echoed so an execute can open exactly the tables a fresh plan would read. */
  readonly targets: readonly RepublishTarget[];
  /**
   * Canonical serialisation of every item. Compared before writing anything, so
   * a record that changed between preview and execute aborts the run instead of
   * being republished as something the operator never saw.
   */
  readonly fingerprint: string;
  readonly items: readonly RepublishItem[];
  readonly blockers: readonly RepublishBlocker[];
  readonly warnings: readonly string[];
  readonly markerId: string;
  readonly markerPresent: boolean;
}

export interface RepublishResult {
  readonly changeIds: readonly string[];
  readonly fingerprint: string;
  readonly markerId: string;
  readonly itemCount: number;
}

/** Thrown instead of writing when anything about the plan is not established. */
export class RepublishBlockedError extends Error {
  readonly blockers: readonly RepublishBlocker[];

  constructor(blockers: readonly RepublishBlocker[]) {
    super(`Republish refused: ${blockers.map((blocker) => blocker.code).join(', ')}`);
    this.name = 'RepublishBlockedError';
    this.blockers = blockers;
  }
}

export const REPUBLISH_MARKER_SCOPE = 'business';
export const REPUBLISH_MARKER_KEY = 'support.republish.entities';
export const SUPPORT_REPUBLISH_APPROVED_DEVICE_ID = '01M67WCBZGJJHF47C65K0S7CRQ';
export const SUPPORT_REPUBLISH_BUSINESS_ID = '01M67WCC02YWSPNQV4FQH6ECET';
export const SUPPORT_REPUBLISH_TARGETS = Object.freeze([
  Object.freeze({ entityType: 'party' as const, entityId: '01M67WDAB87C2JXJJ62PDJ3Q0F' }),
  Object.freeze({ entityType: 'party' as const, entityId: '01M67X21KWA3BM0E0B03NNV25G' }),
  Object.freeze({ entityType: 'party' as const, entityId: '01M67XQMR5TSFZW4MVEGKQDQYV' }),
  Object.freeze({ entityType: 'party' as const, entityId: '01M67ZH8XXD7Y1XQJA5HBVH3P7' }),
  Object.freeze({ entityType: 'product' as const, entityId: '01M67WEH3CHGPKKHF336K32E3D' }),
  Object.freeze({ entityType: 'document' as const, entityId: '01M67WF583ZXBNT4DEFT00T1HD' }),
  Object.freeze({ entityType: 'documentLine' as const, entityId: '01M67WFFTR91VKH2DT5Y9YR8YQ' }),
  Object.freeze({ entityType: 'documentLine' as const, entityId: '01M67WF586TXA56ASTX3PSAXWT' }),
]) satisfies readonly RepublishTarget[];
export const SUPPORT_REPUBLISH_DELETED_LINE_ID = '01M67WF586TXA56ASTX3PSAXWT';
export const SUPPORT_REPUBLISH_DELETED_LINE_VERSION = 2;
export const SUPPORT_REPUBLISH_DELETED_LINE_CHANGE_ID = '01M67WFFTVAYA7G2D4JG0BBK6D';
export const SUPPORT_REPUBLISH_DELETED_LINE_SOURCE_DEVICE_ID =
  SUPPORT_REPUBLISH_APPROVED_DEVICE_ID;
export const SUPPORT_REPUBLISH_DELETED_LINE_CHANGED_AT = '2026-10-31T02:26:34.955Z';
export const SUPPORT_REPUBLISH_DELETED_LINE_DELETED_AT = '2026-10-31T02:26:34.955Z';

function assertSupportRepublishScope(
  businessId: string,
  targets: readonly RepublishTarget[],
): void {
  if (
    businessId !== SUPPORT_REPUBLISH_BUSINESS_ID ||
    targets.length !== SUPPORT_REPUBLISH_TARGETS.length ||
    targets.some((target, index) => {
      const approved = SUPPORT_REPUBLISH_TARGETS[index];
      return !approved || target.entityType !== approved.entityType || target.entityId !== approved.entityId;
    })
  ) {
    throw new RepublishBlockedError([
      {
        code: 'INVALID_SCOPE',
        detail: 'recovery is restricted to the approved Krishna business and eight exact records',
      },
    ]);
  }
}

/** Read the persisted identity without creating or changing one. */
export async function requireApprovedRepublishDevice(db: DNRDatabase): Promise<string> {
  const setting = await db.settings.get(DEVICE_ID_SETTING_ID);
  const deviceId = setting?.value;
  if (
    !setting ||
    setting.id !== DEVICE_ID_SETTING_ID ||
    setting.key !== DEVICE_ID_SETTING_ID ||
    setting.scope !== 'device' ||
    setting.scopeId !== 'app' ||
    typeof deviceId !== 'string' ||
    deviceId.length === 0
  ) {
    throw new RepublishBlockedError([
      {
        code: 'DEVICE_ID_UNAVAILABLE',
        detail: 'the persisted device identity is missing or invalid; recovery is refused',
      },
    ]);
  }
  if (deviceId !== SUPPORT_REPUBLISH_APPROVED_DEVICE_ID) {
    throw new RepublishBlockedError([
      {
        code: 'UNAUTHORIZED_DEVICE',
        detail: 'the persisted device identity is not the approved Edge device',
      },
    ]);
  }
  return deviceId;
}

/**
 * The durable "this was already done" record.
 *
 * Business-scoped within this IndexedDB. It survives app restarts and outbox
 * acknowledgement, but settings are local and are not replicated to other
 * devices or preserved by reinstall. The Edge-only execution gate keeps this
 * durable second-execution guard with the source database.
 */
export function republishMarkerId(businessId: string): string {
  return `${REPUBLISH_MARKER_SCOPE}:${businessId}:${REPUBLISH_MARKER_KEY}`;
}

/**
 * A canonical string for a JSON-shaped value.
 *
 * Key order is an artefact of whichever object produced it, so it is sorted away;
 * `undefined` keys are dropped; array order is MEANING and is preserved. Two
 * values with the same canonical form are the same record for fingerprint
 * purposes.
 *
 * Deliberately synchronous. A `crypto.subtle.digest` here would be an await on a
 * non-Dexie promise inside a Dexie transaction, which commits the transaction
 * underneath the caller — see the same warning in migrations.ts. The fingerprint
 * is therefore compared whole and never hashed.
 */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, item]) => item !== undefined)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return `{${entries.map(([key, item]) => `${JSON.stringify(key)}:${canonicalJson(item)}`).join(',')}}`;
}

/**
 * The fingerprint of a plan, including its scope and all source provenance.
 */
export function republishFingerprint(
  businessId: string,
  targets: readonly RepublishTarget[],
  items: readonly RepublishItem[],
): string {
  return canonicalJson(
    {
      businessId,
      targets,
      items: items.map((item) => ({
        entityType: item.entityType,
        entityId: item.entityId,
        operation: item.operation,
        version: item.version,
        versionSource: item.versionSource,
        fromChangeId: item.fromChangeId,
        changedAt: item.changedAt,
        deletedAt: item.deletedAt,
        payload: item.payload,
      })),
    },
  );
}

export function describeRepublishBlockers(blockers: readonly RepublishBlocker[]): string {
  return blockers
    .map(
      (blocker) =>
        `${blocker.code}${blocker.entityType ? ` ${blocker.entityType}` : ''}${
          blocker.entityId ? ` ${blocker.entityId}` : ''
        }: ${blocker.detail}`,
    )
    .join('\n');
}

/** A human-readable account of what a preview found. */
export function formatRepublishPlan(plan: RepublishPlan): string {
  const lines: string[] = [];
  lines.push(`Business ${plan.businessId}`);
  lines.push(`Device   ${plan.deviceId}`);
  lines.push(`Marker   ${plan.markerId}`);
  lines.push('');

  if (plan.blockers.length > 0) {
    lines.push('REFUSED — nothing will be written:');
    for (const blocker of plan.blockers) lines.push(`  ${describeRepublishBlockers([blocker])}`);
    lines.push('');
  }

  // A refusal with nothing planned is a refusal, not a plan with zero rows: the
  // zero is noise that reads like a successful empty preview.
  if (plan.items.length > 0) {
    lines.push(`${plan.items.length} record(s), each at the version already recorded for it:`);
    for (const item of plan.items) {
      const operation = item.operation === 'DELETE' ? 'DELETE' : 'UPSERT';
      const deletion = item.deletedAt ? ` deletedAt ${item.deletedAt}` : '';
      lines.push(
        `  ${operation} ${item.entityType} ${item.entityId} v${item.version} ` +
          `(from ${item.versionSource})${deletion}`,
      );
    }
  }

  if (plan.warnings.length > 0) {
    lines.push('');
    for (const warning of plan.warnings) lines.push(`  ! ${warning}`);
  }

  return lines.join('\n');
}

/** The newest entry, by version and then by when it was written. */
function newestEntry(entries: readonly SyncOutboxEntry[]): SyncOutboxEntry | undefined {
  return [...entries].sort((a, b) => {
    if (a.version !== b.version) return b.version - a.version;
    return a.createdAt < b.createdAt ? 1 : a.createdAt > b.createdAt ? -1 : 0;
  })[0];
}

function isRecordedVersion(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1;
}

function firstString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return undefined;
}

type TargetOutcome = { readonly item: RepublishItem } | { readonly blocker: RepublishBlocker };

function addTargetBlocker(
  blockers: RepublishBlocker[],
  code: RepublishBlockerCode,
  detail: string,
  target: RepublishTarget,
): void {
  blockers.push({ code, detail, entityType: target.entityType, entityId: target.entityId });
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function validateSupportPayload(
  target: RepublishTarget,
  payload: Record<string, unknown> | undefined,
): string | undefined {
  if (!payload) return 'the original record payload is missing';
  if (payload.id !== target.entityId) return 'payload ID does not match the approved target';
  if (payload.businessId !== SUPPORT_REPUBLISH_BUSINESS_ID) {
    return 'payload is not owned by the approved Krishna business';
  }

  if (target.entityType === 'party') {
    const requiredStrings = [
      'kind', 'code', 'name', 'stateCode', 'stateName', 'createdAt', 'createdBy', 'updatedAt',
      'updatedBy',
    ];
    const missing = requiredStrings.filter((key) => !isNonEmptyString(payload[key]));
    if (missing.length > 0) return `party payload is missing ${missing.join(', ')}`;
    if (payload.kind !== 'CUSTOMER' && payload.kind !== 'SUPPLIER') {
      return 'party kind is invalid';
    }
    if (
      !Array.isArray(payload.billingAddressLines) ||
      !payload.billingAddressLines.every((line) => typeof line === 'string') ||
      !Array.isArray(payload.tags) ||
      !payload.tags.every((tag) => typeof tag === 'string') ||
      typeof payload.creditDays !== 'number' ||
      !Number.isInteger(payload.creditDays) ||
      payload.creditDays < 0 ||
      !Number.isSafeInteger(payload.openingBalancePaise) ||
      !/^\d{2}$/.test(String(payload.stateCode)) ||
      !isValidTimestamp(payload.createdAt) ||
      !isValidTimestamp(payload.updatedAt) ||
      typeof payload.isActive !== 'boolean'
    ) {
      return 'party payload has invalid address, tags, credit, balance, or active fields';
    }
    return;
  }

  if (target.entityType === 'product') {
    const requiredStrings = [
      'sku', 'name', 'kind', 'unit', 'taxRateId', 'createdAt', 'createdBy', 'updatedAt',
      'updatedBy',
    ];
    const missing = requiredStrings.filter((key) => !isNonEmptyString(payload[key]));
    if (missing.length > 0) return `product payload is missing ${missing.join(', ')}`;
    const requiredNumbers = [
      'unitPrecision', 'conversionNumerator', 'conversionDenominator', 'cessRateBp',
      'salePricePaise', 'purchasePricePaise',
    ];
    if (requiredNumbers.some((key) => !Number.isSafeInteger(payload[key]))) {
      return 'product payload has an invalid numeric field';
    }
    if (
      (payload.kind !== 'GOODS' && payload.kind !== 'SERVICE') ||
      typeof payload.cessRateBp !== 'number' ||
      payload.cessRateBp < 0 ||
      payload.cessRateBp > 10_000 ||
      !isValidTimestamp(payload.createdAt) ||
      !isValidTimestamp(payload.updatedAt) ||
      typeof payload.isStockTracked !== 'boolean' ||
      typeof payload.isActive !== 'boolean'
    ) {
      return 'product payload has invalid tracking or active fields';
    }
    return;
  }

  const parsed =
    target.entityType === 'document'
      ? documentSchema.safeParse(payload)
      : target.entityType === 'documentLine'
        ? documentLineSchema.safeParse(payload)
        : undefined;
  if (!parsed) return `unsupported entity type ${target.entityType}`;
  if (!parsed.success) {
    return `${target.entityType} payload is incomplete or invalid: ${parsed.error.issues
      .slice(0, 3)
      .map((issue) => issue.path.join('.') || 'payload')
      .join(', ')}`;
  }
  return;
}

function isValidTimestamp(value: unknown): value is string {
  return isNonEmptyString(value) && !Number.isNaN(Date.parse(value));
}

async function validateSupportBundle(
  db: DNRDatabase,
  targets: readonly RepublishTarget[],
  items: readonly RepublishItem[],
  blockers: RepublishBlocker[],
): Promise<void> {
  const itemsByKey = new Map(items.map((item) => [`${item.entityType}:${item.entityId}`, item]));
  const payloads = new Map<string, Record<string, unknown>>();
  for (const target of targets) {
    const item = itemsByKey.get(`${target.entityType}:${target.entityId}`);
    const isDeletedTarget =
      target.entityType === 'documentLine' &&
      target.entityId === SUPPORT_REPUBLISH_DELETED_LINE_ID;
    let payload = item?.payload;
    if (isDeletedTarget && item?.operation === 'DELETE') {
      const row = (await db.documentLines.get(target.entityId)) as
        | Record<string, unknown>
        | undefined;
      if (row) {
        payload = row;
      } else {
        const upsert = await db.syncOutbox
          .where('[businessId+entityType+entityId]')
          .equals([SUPPORT_REPUBLISH_BUSINESS_ID, 'documentLine', target.entityId])
          .filter((entry) => entry.operation === 'UPSERT' && Boolean(entry.payload))
          .first();
        payload = upsert?.payload;
      }
    }
    if (
      item &&
      ((!isDeletedTarget && item.operation !== 'UPSERT') ||
        (isDeletedTarget && item.operation !== 'DELETE') ||
        !isValidTimestamp(item.changedAt))
    ) {
      addTargetBlocker(
        blockers,
        'INVALID_PAYLOAD',
        'operation or original change timestamp does not match the approved record type',
        target,
      );
    }
    const validationError = validateSupportPayload(target, payload);
    if (validationError) {
      addTargetBlocker(blockers, 'INVALID_PAYLOAD', validationError, target);
      continue;
    }
    if (payload) payloads.set(`${target.entityType}:${target.entityId}`, payload);
  }

  const partyIds = new Set(
    targets.filter((target) => target.entityType === 'party').map((target) => target.entityId),
  );
  const invoiceTarget = targets.find((target) => target.entityType === 'document');
  const invoicePayload = invoiceTarget
    ? payloads.get(`document:${invoiceTarget.entityId}`)
    : undefined;
  if (
    invoiceTarget &&
    invoicePayload &&
    (typeof invoicePayload.customerId !== 'string' ||
      !partyIds.has(invoicePayload.customerId) ||
      !invoicePayload.partySnapshot ||
      typeof invoicePayload.partySnapshot !== 'object' ||
      (invoicePayload.partySnapshot as Record<string, unknown>).id !== invoicePayload.customerId)
  ) {
    addTargetBlocker(
      blockers,
      'INVALID_RELATIONSHIP',
      'invoice customer and party snapshot must reference one of the approved parties',
      invoiceTarget,
    );
  }

  for (const target of targets.filter((candidate) => candidate.entityType === 'documentLine')) {
    const payload = payloads.get(`documentLine:${target.entityId}`);
    if (!payload) continue;
    if (
      !invoiceTarget ||
      payload.documentId !== invoiceTarget.entityId ||
      (invoicePayload && payload.docType !== invoicePayload.docType) ||
      payload.productId !==
        targets.find((candidate) => candidate.entityType === 'product')?.entityId
    ) {
      addTargetBlocker(
        blockers,
        'INVALID_RELATIONSHIP',
        'invoice line must reference the approved invoice and product',
        target,
      );
    }
  }
}

async function planApprovedDelete(
  db: DNRDatabase,
  request: RepublishRequest,
  target: RepublishTarget,
  row: Record<string, unknown> | undefined,
): Promise<TargetOutcome> {
  const relatedEntries = await db.syncOutbox
    .where('[entityType+entityId]')
    .equals([target.entityType, target.entityId])
    .toArray();
  const deleteEntries = relatedEntries.filter((entry) => entry.operation === 'DELETE');
  const [entry] = deleteEntries;
  const invalid =
    deleteEntries.length !== 1 ||
    !entry ||
    entry.businessId !== SUPPORT_REPUBLISH_BUSINESS_ID ||
    entry.deviceId !== SUPPORT_REPUBLISH_DELETED_LINE_SOURCE_DEVICE_ID ||
    entry.entityType !== 'documentLine' ||
    entry.entityId !== SUPPORT_REPUBLISH_DELETED_LINE_ID ||
    entry.changeId !== SUPPORT_REPUBLISH_DELETED_LINE_CHANGE_ID ||
    entry.version !== SUPPORT_REPUBLISH_DELETED_LINE_VERSION ||
    entry.status !== 'acked' ||
    entry.changedAt !== SUPPORT_REPUBLISH_DELETED_LINE_CHANGED_AT ||
    entry.deletedAt !== SUPPORT_REPUBLISH_DELETED_LINE_DELETED_AT ||
    entry.payload !== undefined ||
    (row !== undefined &&
    (row.syncVersion !== SUPPORT_REPUBLISH_DELETED_LINE_VERSION ||
      row.syncChangeId !== SUPPORT_REPUBLISH_DELETED_LINE_CHANGE_ID ||
      row.syncDeletedAt !== SUPPORT_REPUBLISH_DELETED_LINE_DELETED_AT));
  if (invalid || !entry) {
    return {
      blocker: {
        code: 'DELETE_PROVENANCE_INVALID',
        detail:
          'the exact acknowledged original DELETE for this business, line, version 2, change ID, ' +
          'changedAt, and deletedAt could not be verified',
        entityType: target.entityType,
        entityId: target.entityId,
      },
    };
  }
  const otherPending = relatedEntries.find(
    (candidate) => candidate.businessId === request.businessId && candidate.status === 'pending',
  );
  if (otherPending) {
    return {
      blocker: {
        code: 'ALREADY_PENDING',
        detail: `outbox entry ${otherPending.changeId} is still pending`,
        entityType: target.entityType,
        entityId: target.entityId,
      },
    };
  }
  return {
    item: {
      entityType: target.entityType,
      entityId: target.entityId,
      operation: 'DELETE',
      version: SUPPORT_REPUBLISH_DELETED_LINE_VERSION,
      versionSource: 'outbox',
      changedAt: entry.changedAt,
      deletedAt: entry.deletedAt,
      fromChangeId: entry.changeId,
    },
  };
}

async function planTarget(
  db: DNRDatabase,
  request: RepublishRequest,
  target: RepublishTarget,
  warnings: string[],
): Promise<TargetOutcome> {
  const tableName = tableNameFor(target.entityType);
  if (!tableName) {
    return {
      blocker: {
        code: 'NO_TABLE',
        detail: 'no local table holds this entity type',
        entityType: target.entityType,
        entityId: target.entityId,
      },
    };
  }

  const entries = await db.syncOutbox
    .where('[businessId+entityType+entityId]')
    .equals([request.businessId, target.entityType, target.entityId])
    .toArray();

  const row = (await db.table(tableName).get(target.entityId)) as
    | Record<string, unknown>
    | undefined;

  if (row && typeof row.businessId === 'string' && row.businessId !== request.businessId) {
    return {
      blocker: {
        code: 'FOREIGN_ROW',
        detail: `the local row belongs to ${row.businessId}, not ${request.businessId}`,
        entityType: target.entityType,
        entityId: target.entityId,
      },
    };
  }

  if (
    target.entityType === 'documentLine' &&
    target.entityId === SUPPORT_REPUBLISH_DELETED_LINE_ID
  ) {
    return planApprovedDelete(db, request, target, row);
  }

  const pending = entries.find((entry) => entry.status === 'pending');
  if (pending) {
    return {
      blocker: {
        code: 'ALREADY_PENDING',
        detail: `outbox entry ${pending.changeId} is still pending; let the existing queue drain first`,
        entityType: target.entityType,
        entityId: target.entityId,
      },
    };
  }

  const failed = entries.find((entry) => entry.status === 'failed');
  if (failed) {
    warnings.push(
      `${target.entityType} ${target.entityId}: outbox entry ${failed.changeId} is recorded as ` +
        'failed; the republished change is a second, independent attempt at the same version.',
    );
  }

  const deleteEntry = newestEntry(entries.filter((entry) => entry.operation === 'DELETE'));
  const upsertEntry = newestEntry(entries.filter((entry) => entry.operation === 'UPSERT'));
  const tombstoneAt = firstString(row?.syncDeletedAt);

  let operation: RepublishOperation;
  if (tombstoneAt) {
    operation = 'DELETE';
  } else if (row) {
    operation = 'UPSERT';
    if (deleteEntry) {
      warnings.push(
        `${target.entityType} ${target.entityId}: a DELETE (${deleteEntry.changeId}) is in the ` +
          'outbox but the local row is live; the live row wins and is republished as an UPSERT.',
      );
    }
  } else if (deleteEntry) {
    // The row is gone because the DELETE was applied. The entry is the record.
    operation = 'DELETE';
  } else {
    return {
      blocker: {
        code: 'MISSING_RECORD',
        detail:
          'neither a local row nor a DELETE outbox entry exists, so there is no version or ' +
          'deletion timestamp to republish',
        entityType: target.entityType,
        entityId: target.entityId,
      },
    };
  }

  const source = operation === 'DELETE' ? (deleteEntry ?? upsertEntry) : (upsertEntry ?? deleteEntry);

  let version: number | undefined;
  let versionSource: RepublishVersionSource | undefined;
  if (source && isRecordedVersion(source.version)) {
    version = source.version;
    versionSource = 'outbox';
  } else if (isRecordedVersion(row?.syncVersion)) {
    version = row.syncVersion;
    versionSource = 'row';
  } else if (operation === 'UPSERT' && target.entityType === 'documentLine') {
    version = 1;
    versionSource = 'documentLine-upsert-invariant';
    warnings.push(
      `${target.entityType} ${target.entityId}: no recorded version, republishing at 1 because ` +
        'every document line UPSERT is published at version 1 and a line is replaced, never edited.',
    );
  }

  if (version === undefined || versionSource === undefined) {
    return {
      blocker: {
        code: 'VERSION_UNRESOLVED',
        detail:
          'no outbox entry and no row version at or above 1; a version below 1 is refused by the ' +
          'server and would reject the entire batch',
        entityType: target.entityType,
        entityId: target.entityId,
      },
    };
  }

  let changedAt: string | undefined;
  let deletedAt: string | undefined;
  let payload: Record<string, unknown> | undefined;

  if (operation === 'DELETE') {
    deletedAt = tombstoneAt ?? firstString(deleteEntry?.deletedAt, upsertEntry?.deletedAt);
    if (!deletedAt) {
      return {
        blocker: {
          code: 'DELETE_WITHOUT_TIMESTAMP',
          detail:
            'no deletion timestamp on the row or in the outbox; a tombstone without one cannot be ' +
            'aged out and is refused by the receiving device',
          entityType: target.entityType,
          entityId: target.entityId,
        },
      };
    }
    changedAt = deletedAt;
  } else {
    if (!row) {
      return {
        blocker: {
          code: 'MISSING_RECORD',
          detail: 'the row vanished between the operation decision and the read',
          entityType: target.entityType,
          entityId: target.entityId,
        },
      };
    }
    changedAt = firstString(row.syncUpdatedAt, row.updatedAt, upsertEntry?.changedAt);
    if (!changedAt) {
      return {
        blocker: {
          code: 'CHANGED_AT_UNRESOLVED',
          detail: 'the row carries no update timestamp to publish with',
          entityType: target.entityType,
          entityId: target.entityId,
        },
      };
    }
    payload = row;
  }

  return {
    item: {
      entityType: target.entityType,
      entityId: target.entityId,
      operation,
      version,
      versionSource,
      changedAt,
      ...(payload ? { payload } : {}),
      ...(deletedAt ? { deletedAt } : {}),
      fromChangeId: source?.changeId ?? null,
    },
  };
}

/**
 * Read-only. Writes nothing, anywhere.
 *
 * Callable inside an open transaction: every read it performs goes through `db`,
 * which Dexie routes to the caller's transaction.
 */
export async function planRepublish(
  db: DNRDatabase,
  request: RepublishRequest,
): Promise<RepublishPlan> {
  const blockers: RepublishBlocker[] = [];
  const warnings: string[] = [];
  const items: RepublishItem[] = [];
  const markerId = republishMarkerId(request.businessId);

  if (request.targets.length === 0) {
    blockers.push({
      code: 'NOTHING_TO_REPUBLISH',
      detail: 'no targets were supplied, so there is nothing to plan',
    });
  }

  const marker = await db.settings.get(markerId);
  if (marker) {
    blockers.push({
      code: 'ALREADY_EXECUTED',
      detail:
        `marker ${markerId} was written at ${marker.updatedAt}; a recovery already ran for this ` +
        'business and must not run a second time',
    });
    return {
      businessId: request.businessId,
      deviceId: request.deviceId,
      targets: request.targets,
      fingerprint: republishFingerprint(request.businessId, request.targets, items),
      items,
      blockers,
      warnings,
      markerId,
      markerPresent: true,
    };
  }

  for (const target of request.targets) {
    const outcome = await planTarget(db, request, target, warnings);
    if ('blocker' in outcome) blockers.push(outcome.blocker);
    else items.push(outcome.item);
  }

  if (
    request.businessId === SUPPORT_REPUBLISH_BUSINESS_ID &&
    request.targets.length === SUPPORT_REPUBLISH_TARGETS.length &&
    request.targets.every(
      (target, index) =>
        target.entityType === SUPPORT_REPUBLISH_TARGETS[index]?.entityType &&
        target.entityId === SUPPORT_REPUBLISH_TARGETS[index]?.entityId,
    )
  ) {
    await validateSupportBundle(db, request.targets, items, blockers);
  }

  return {
    businessId: request.businessId,
    deviceId: request.deviceId,
    targets: request.targets,
    fingerprint: republishFingerprint(request.businessId, request.targets, items),
    items,
    blockers,
    warnings,
    markerId,
    markerPresent: Boolean(marker),
  };
}

/** Preview only after independently resolving the persisted device identity. */
export async function previewRepublish(
  db: DNRDatabase,
  request: RepublishPreviewRequest,
): Promise<RepublishPlan> {
  const deviceId = await requireApprovedRepublishDevice(db);
  assertSupportRepublishScope(request.businessId, request.targets);
  return planRepublish(db, { ...request, deviceId });
}

/**
 * Queue the plan, atomically, and record that it happened.
 *
 * Everything happens in ONE transaction: the re-plan, the outbox rows, the
 * `syncChangeId` stamps `recordBusinessMutations` writes onto the rows, and the
 * marker. A failure at any point leaves the database exactly as it was, so
 * "half a recovery" is not a state this can reach.
 *
 * The plan is re-derived INSIDE the transaction and compared by fingerprint with
 * the one the operator approved. A row edited in another tab, a version that
 * moved, a deletion that was already applied elsewhere — any of these changes the
 * fingerprint and aborts before a single entry is written.
 */
export async function executeRepublish(
  db: DNRDatabase,
  plan: RepublishPlan,
  input: { readonly now: string },
): Promise<RepublishResult> {
  const deviceId = await requireApprovedRepublishDevice(db);
  if (plan.deviceId !== deviceId) {
    throw new RepublishBlockedError([
      {
        code: 'PLAN_DEVICE_MISMATCH',
        detail: 'the preview was created for a different device identity',
      },
    ]);
  }

  assertSupportRepublishScope(plan.businessId, plan.targets);

  if (republishFingerprint(plan.businessId, plan.targets, plan.items) !== plan.fingerprint) {
    throw new RepublishBlockedError([
      {
        code: 'FINGERPRINT_CHANGED',
        detail: 'the supplied Preview plan was modified after it was generated',
      },
    ]);
  }
  if (
    plan.items.length !== SUPPORT_REPUBLISH_TARGETS.length ||
    plan.items.some(
      (item, index) =>
        item.entityType !== SUPPORT_REPUBLISH_TARGETS[index]?.entityType ||
        item.entityId !== SUPPORT_REPUBLISH_TARGETS[index]?.entityId,
    )
  ) {
    throw new RepublishBlockedError([
      {
        code: 'INVALID_SCOPE',
        detail: 'the supplied Preview does not contain exactly the approved target records',
      },
    ]);
  }

  if (plan.blockers.length > 0) throw new RepublishBlockedError(plan.blockers);

  // Every table the fresh plan could read, derived from the plan's own targets so
  // Dexie's transaction scope cannot be short by one entity type.
  const tableNames = new Set<string>();
  for (const target of plan.targets) {
    const tableName = tableNameFor(target.entityType);
    if (tableName) tableNames.add(tableName);
  }
  const scopedTables = [...tableNames].map((tableName) => db.table(tableName));

  return db.transaction('rw', [db.syncOutbox, db.settings, ...scopedTables], async () => {
    const executionDeviceId = await requireApprovedRepublishDevice(db);
    assertSupportRepublishScope(plan.businessId, plan.targets);
    if (executionDeviceId !== deviceId) {
      throw new RepublishBlockedError([
        {
          code: 'UNAUTHORIZED_DEVICE',
          detail: 'the persisted device identity changed before execution',
        },
      ]);
    }

    const fresh = await planRepublish(db, {
      businessId: plan.businessId,
      deviceId: executionDeviceId,
      targets: plan.targets,
      now: input.now,
    });

    if (fresh.blockers.length > 0) throw new RepublishBlockedError(fresh.blockers);

    if (fresh.fingerprint !== plan.fingerprint) {
      throw new RepublishBlockedError([
        {
          code: 'FINGERPRINT_CHANGED',
          detail:
            'a record changed between the preview and this confirmation; run the preview again ' +
            'and read the new plan before republishing',
        },
      ]);
    }

    const queuedAt = Date.parse(input.now);
    if (!Number.isFinite(queuedAt)) {
      throw new Error('Republish execution timestamp must be a valid ISO date-time.');
    }

    const inputs: RecordBusinessMutation[] = fresh.items.map((item, index) => ({
      businessId: fresh.businessId,
      deviceId: fresh.deviceId,
      entityType: item.entityType,
      entityId: item.entityId,
      operation: item.operation,
      version: item.version,
      changedAt: item.changedAt,
      queuedAt: new Date(queuedAt + index).toISOString(),
      ...(item.payload ? { payload: item.payload } : {}),
      ...(item.deletedAt ? { deletedAt: item.deletedAt } : {}),
    }));

    // Fresh change ids: this is a NEW mutation of the same content, not a retry
    // of an id the cloud may have pruned. A reused id would either be rejected
    // (different content) or silently ignored (identical content) — the second
    // is the failure this whole module exists to avoid.
    const changeIds = await recordBusinessMutations(db, inputs);

    const marker: Setting = {
      id: fresh.markerId,
      scope: 'business',
      scopeId: fresh.businessId,
      businessId: fresh.businessId,
      key: REPUBLISH_MARKER_KEY,
      value: {
        fingerprint: fresh.fingerprint,
        changeIds,
        records: fresh.items.map(
          (item) =>
            `${item.entityType}:${item.entityId}@${item.version}` +
            (item.operation === 'DELETE' ? ':DELETE' : ''),
        ),
        republishedAt: input.now,
        deviceId: fresh.deviceId,
      },
      updatedAt: input.now,
      updatedBy: fresh.deviceId,
    };
    await db.settings.put(marker);

    return {
      changeIds,
      fingerprint: fresh.fingerprint,
      markerId: fresh.markerId,
      itemCount: fresh.items.length,
    };
  });
}
